import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'

import { Button } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Props for CtaChecklist component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Split layout with content left, checklist right
 * - Muted background card
 * - Single primary button with arrow icon (1 regular button)
 * - **NEVER use this for install commands - use CtaCenteredCheck instead**
 * - Best for feature highlights with benefits list
 *
 * **CTA Button Rules (CRITICAL):**
 * 1. Use this component for 1 regular button
 * 2. Use CtaCenteredCheck for npm/npx/pnpm install commands (InstallSnippet ONLY)
 * 3. NEVER mix InstallSnippet with regular buttons - they are mutually exclusive
 */
export interface CtaChecklistProps {
  /** Main heading text */
  heading?: string
  /** Description text below heading */
  description?: string
  /** Button configuration */
  button?: {
    text: string
    url: string
  }
  /** List of benefit/feature items with checkmarks */
  items?: string[]
  /** Additional CSS classes */
  className?: string
}

const defaultItems = [
  'Easy Integration',
  '24/7 Support',
  'Customizable Design',
  'Scalable Performance',
  'Hundreds of Blocks',
]

/**
 * CtaChecklist - Split CTA with checklist
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────┐
 * │  ┌─────────────────┬──────────────────┐│
 * │  │  Heading        │  ✓ Item 1        ││
 * │  │  Description    │  ✓ Item 2        ││
 * │  │  [Button →]     │  ✓ Item 3        ││
 * │  │                 │  ✓ Item 4        ││
 * │  └─────────────────┴──────────────────┘│
 * └────────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Feature highlights with benefits
 * - Pricing page CTAs
 * - Sign-up prompts with value props
 *
 * @example
 * ```tsx
 * <CtaChecklist
 *   heading="Get Started Today"
 *   description="Join thousands of satisfied customers."
 *   button={{ text: 'Sign Up', url: '/signup' }}
 *   items={['Free trial', '24/7 support', 'No credit card']}
 * />
 * ```
 */
const CtaChecklist = ({
  heading = 'Get Started Today',
  description = 'Join thousands of satisfied customers and transform your workflow with our powerful platform.',
  button = {
    text: 'Get Started',
    url: '#',
  },
  items = defaultItems,
  className,
}: CtaChecklistProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const listAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="flex justify-center">
          <div className="flex flex-col items-start justify-between gap-8 rounded-[var(--radius)] bg-muted px-4 py-10 md:flex-row md:px-12 lg:px-16 lg:py-24">
            <motion.div className="md:w-1/2" variants={contentAnimation}>
              <h4 className="mb-1 text-2xl font-semibold md:text-3xl">
                {heading}
              </h4>
              <Markdown size="base">{description}</Markdown>
              {button && (
                <Button className="mt-6 w-full sm:w-auto" asChild>
                  <a href={button.url}>
                    {button.text} <ArrowRight className="ml-2 size-4" />
                  </a>
                </Button>
              )}
            </motion.div>
            <motion.div className="md:w-1/3" variants={listAnimation}>
              <ul className="flex flex-col space-y-2 text-sm font-medium">
                {items.map((item) => (
                  <li className="flex items-center" key={item}>
                    <Check className="mr-4 size-4 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export { CtaChecklist }
